import { SidePanelState } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';

export type TranscriptEvidence = {
  evidence: string[];
  /** Parallel to `evidence` — excerpt `[MM:SS]` from inline View in Transcript. */
  evidenceTimestamps?: Array<string | undefined>;
  isShowing: boolean;
  sessionId: string;
  entityId: string;
  /** When true, partial word-run matching is allowed for inline View in Transcript. */
  allowWordRunFallback?: boolean;
  returnOpensEntityVerificationDrawer?: boolean;
};

/** Inline View in Transcript (not entity verification or embeddings evidence). */
export const isInlineViewInTranscriptEvidence = (
  evidence: TranscriptEvidence | null | undefined,
  sessionId?: string
): boolean =>
  Boolean(
    evidence?.isShowing &&
    evidence.sessionId === sessionId &&
    evidence.returnOpensEntityVerificationDrawer !== true &&
    evidence.evidenceTimestamps !== undefined
  );

export const transcriptEvidenceAtom = atom<TranscriptEvidence | null>(null);

export type NoteEvidence = {
  isShowing: boolean;
  entityId: string | null;
  highlightRanges: Array<{
    start: number;
    end: number;
    text: string;
  }>;
};

export const noteEvidenceAtom = atom<NoteEvidence | null>(null);

export const noteEvidenceNotFoundAtom = atom<boolean>(false);

export const isGeneratingTasksAtom = atom<Record<string, boolean>>({});

/**
 * Base atoms — not exported directly. All reads/writes go through the
 * derived atoms below so that opening one panel automatically closes the others.
 */
const _activePanelBase = atom<SidePanelState>(SidePanelState.CLOSED);
const _evDrawerOpenBase = atom<boolean>(false);
const _verifyLiteDrawerOpenBase = atom<boolean>(false);
const _evidenceSidebarBase = atom<boolean>(false);

export const activePanelAtom = atom(
  (get) => get(_activePanelBase),
  (get, set, next: SidePanelState) => {
    set(_activePanelBase, next);
    if (next !== SidePanelState.CLOSED) {
      set(_evDrawerOpenBase, false);
      set(_verifyLiteDrawerOpenBase, false);
      set(_evidenceSidebarBase, false);
    }
  }
);

export const entityVerificationDrawerOpenAtom = atom(
  (get) => get(_evDrawerOpenBase),
  (get, set, next: boolean) => {
    set(_evDrawerOpenBase, next);
    if (next) {
      set(_activePanelBase, SidePanelState.CLOSED);
      set(_verifyLiteDrawerOpenBase, false);
      set(_evidenceSidebarBase, false);
    }
  }
);

/** Opens Verify Lite and closes other right-side panels. */
export const verifyLiteDrawerOpenAtom = atom(
  (get) => get(_verifyLiteDrawerOpenBase),
  (get, set, next: boolean) => {
    set(_verifyLiteDrawerOpenBase, next);
    if (next) {
      set(_activePanelBase, SidePanelState.CLOSED);
      set(_evDrawerOpenBase, false);
      set(_evidenceSidebarBase, false);
    }
  }
);

/**
 * Controls whether the right-side Evidence sidebar panel is open.
 * Opening evidence automatically closes the tasks/codes panel and the
 * verify drawers. Opening any of those panels closes evidence in return.
 * Manual reopening is always allowed.
 */
export const evidenceSidebarOpenAtom = atom(
  (get) => get(_evidenceSidebarBase),
  (get, set, next: boolean | ((prev: boolean) => boolean)) => {
    const nextValue = typeof next === 'function' ? next(get(_evidenceSidebarBase)) : next;
    set(_evidenceSidebarBase, nextValue);
    if (nextValue) {
      set(_activePanelBase, SidePanelState.CLOSED);
      set(_evDrawerOpenBase, false);
      set(_verifyLiteDrawerOpenBase, false);
    }
  }
);

export const embeddingsPanelSessionIdAtom = atom<string | null>(null);

/**
 * Per-session collapse state for the V1 in-session protocol nudges panel.
 *
 * Absent / `false` = open, so newly detected protocols still surface
 * automatically. The bottom-toolbar Protocols toggle flips the flag for the
 * active session, giving the panel a non-destructive close that can always be
 * reopened — the affordance that was missing when the only way to "close" the
 * panel was dismissing every card, which left no way back.
 *
 * Keyed by session id (like `isGeneratingTasksAtom`) so collapsing one
 * session's panel doesn't bleed into the next.
 */
export const nudgesPanelClosedBySessionAtom = atom<Record<string, boolean>>({});

export const activeEvidenceTaskIdAtom = atom<string | null>(null);

export type PendingNoteEvidence = {
  evidence: string;
  taskId: string;
  sessionId: string;
};

export const pendingNoteEvidenceAtom = atom<PendingNoteEvidence | null>(null);
